import { Grid2 } from "@mui/material";
import { MemberDetails } from "@pages/project-details/project-overview/member-details";
import { MainProjectDetails } from "@pages/project-details/project-overview/main-project-details";
import { DateDetails } from "@pages/project-details/project-overview/date-details";
import { SecondaryProjectDetails } from "@pages/project-details/project-overview/secondary-project-details";
import { ProjectDescription } from "@pages/project-details/project-overview/project-description";
import { CSSProperties, useMemo } from "react";
import { useAppSelector } from "@store/store-helper";
import { isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { SdbProject } from "@custom-types/project-types";
import { ProjectThumbnailImage } from "@pages/project-details/project-overview/project-thumbnail/project-thumbnail-image";
import { CommonStyles } from "@styles/common-styles";
import { ProjectChart } from "@pages/project-details/project-overview/project-chart";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

interface Props {
  /** The project to display in the ProjectOverview */
  project?: SdbProject | null;

  /** Whether the project, details and/or project context are being fetched */
  isLoading: boolean;
}

/** Defines the border style for all individual sections */
const borderStyle: CSSProperties = {
  border: CommonStyles.Borders.gray200Divider,
};

/** Defines the default padding inside each individual section */
const defaultPadding: CSSProperties = {
  padding: "20px",
};

/**
 * Defines the separation between individual sections.
 * To keep things centered the separation is applied to both sides,
 * meaning that the actual separation will be two times this value.
 */
const boxesSeparation = "10px";

/**
 * Contains the Overview tab of the project details
 */
export function ProjectOverview({ project, isLoading }: Props): JSX.Element {
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const { hasUserPermissionProjectLevel } =
    useHasUserValidPermissionProjectLevel();

  /**
   * Check if the user can view the members tab in project overview page
   * and view all users with access to the project at the company level
   */
  const { canViewCompanyMembers } = useHasUserValidFeatureCompanyLevel();

  const { shouldShowOpenInWebEditorBtn } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  const shouldShowSkeleton = isLoading || !project;

  const shouldShowStats = useMemo(() => {
    // Early exit with false while the project and its context is being fetched/loaded
    if (isLoading || !shouldShowOpenInWebEditorBtn) {
      return false;
    }

    return hasUserPermissionProjectLevel({
      permissionName: RequiredPermissionProjectLevelName.canViewProjectsStats,
      project,
    });
  }, [
    hasUserPermissionProjectLevel,
    isLoading,
    project,
    shouldShowOpenInWebEditorBtn,
  ]);

  const shouldShowTeamMembers = useMemo(() => {
    // Early exit with false while the project and its context is being fetched/loaded
    if (isLoading) {
      return false;
    }

    return (
      hasUserPermissionProjectLevel({
        permissionName:
          RequiredPermissionProjectLevelName.canViewAllProjectMembers,
        project,
      }) || canViewCompanyMembers
    );
  }, [
    hasUserPermissionProjectLevel,
    canViewCompanyMembers,
    isLoading,
    project,
  ]);

  return (
    <>
      {/* Page info bar for project name, group and project manager */}
      <MainProjectDetails
        project={project}
        isProjectEditable={isProjectEditable}
        isLoading={shouldShowSkeleton}
      />
      {/* Grid2 for the whole page */}
      <Grid2 container sx={{ mt: "12px" }} width={"100%"}>
        {/* Left side */}
        <Grid2
          columns={12}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          {/* Project details like date, address */}
          <Grid2
            sx={{
              ...defaultPadding,
              ...borderStyle,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Date details like creation date and last captured */}
            <DateDetails project={project} isLoading={shouldShowSkeleton} />

            {/* Secondary details to show client, address, access level and area */}
            <SecondaryProjectDetails
              project={project}
              isProjectEditable={isProjectEditable}
              isLoading={shouldShowSkeleton}
            />

            {/* Project member avatars */}
            {/* Only show project members if the members property is defined in the project details object */}
            {shouldShowTeamMembers &&
              !shouldShowSkeleton &&
              project.members.length > 0 && (
                <Grid2
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                  }}
                >
                  <MemberDetails />
                </Grid2>
              )}
          </Grid2>
        </Grid2>

        {/* Right side */}
        <Grid2
          container
          columns={12}
          sx={{
            paddingTop: {
              xs: boxesSeparation,
              md: 0,
            },
            paddingLeft: {
              md: boxesSeparation,
            },
          }}
          size={{
            xs: 12,
            md: 7,
          }}
        >
          {/* Left part of right side */}
          <Grid2
            sx={{
              paddingBottom: {
                xs: boxesSeparation,
                lg: shouldShowStats ? boxesSeparation : 0,
              },
              paddingRight: {
                lg: boxesSeparation,
              },
              height: "auto",
            }}
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 6,
            }}
          >
            {/* Project description */}
            <Grid2
              sx={{
                ...defaultPadding,
                ...borderStyle,
                height: "100%",
              }}
            >
              <ProjectDescription
                project={project}
                isProjectEditable={isProjectEditable}
                isLoading={shouldShowSkeleton}
              />
            </Grid2>
          </Grid2>
          {/* Right part of right side */}
          <Grid2
            sx={{
              ...borderStyle,
              mb: shouldShowStats ? boxesSeparation : 0,
              height: "auto",
              display: "flex",
              alignItems: "center",
            }}
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 6,
            }}
          >
            <ProjectThumbnailImage
              project={project}
              isLoading={shouldShowSkeleton}
              isProjectEditable={isProjectEditable}
            />
          </Grid2>
          {/* Project stats chart */}
          {shouldShowStats && (
            <Grid2
              sx={{
                ...borderStyle,
                ...defaultPadding,
              }}
              size={12}
            >
              <ProjectChart isLoading={shouldShowSkeleton} />
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </>
  );
}
